import debug from 'debug';
import { components } from '../connections/integro-api-types';
import { Application, Poller } from '@integro/core';

const log = debug('itp:integro.helpers.AsyncCommandPoller');

const OP_READ_ASYNC_COMMAND = 'read_async_command';
const POLL_INTERVAL = 1000;

type AsyncCommand = components['schemas']['AsyncCommand'];

export class AsyncCommandPoller {
  app: Application;
  poller?: Poller;
  data?: AsyncCommand;
  error?: any;

  isPending: boolean = false;
  isCompleted: boolean = false;
  isRunning: boolean = false;
  isErrored: boolean = false;

  onError: CallableFunction | null;
  onCompleted: CallableFunction | null;

  constructor(
    app: Application,
    onError: CallableFunction | null = null,
    onCompleted: CallableFunction | null = null
  ) {
    this.app = app;
    this.data = null;
    this.error = null;
    this.onError = onError;
    this.onCompleted = onCompleted;
  }

  begin(job: Promise<AsyncCommand>) {
    this.isRunning = true;

    Promise.resolve(job)
      .then(d => this.init(d))
      .catch(err => this.updateState(null, err));
  }

  stop() {
    this.poller?.stop();
  }

  private init(data: AsyncCommand) {
    this.poller = new Poller({
      name: 'AsyncCommandPoller',
      callback: this.pollerCallback.bind(this),
      interval: POLL_INTERVAL,
      errback: err => this.onError && this.onError(err)
    });
    this.updateState(data, null);
    log('polling started');
  }

  async fetchCommand(id: string) {
    return this.app.connections
      .request(OP_READ_ASYNC_COMMAND, {
        parameters: { id }
      })
      .then(resp => resp.data as AsyncCommand);
  }

  private async pollerCallback() {
    try {
      const data = await this.fetchCommand(this.data!.id);
      return this.updateState(data, null);
    } catch (err) {
      return this.updateState(null, err);
    }
  }

  private updateState(data: AsyncCommand, error: Error | null = null) {
    try {
      let statusChanged =
        (!this.data && data) ||
        (this.data && data && this.data.status !== data.status);

      if (data) {
        this.data = data;
        this.isPending = data.status === 'pending';
        this.isCompleted = data.status === 'completed';
      }

      this.setErrorStates(error, data);

      if (this.isPending && this.poller) {
        this.poller.start();
      }

      if (this.isRunning && !this.poller.isRunning) {
        this.poller.start();
      }

      if (this.isCompleted && statusChanged) {
        this.complete();
      }

      if (
        (this.isCompleted || this.isErrored) &&
        this.poller &&
        this.poller.isRunning
      ) {
        this.poller.stop();
      }

      log(
        'state changed data: %o, error: %o, downloader: %o',
        this.data,
        error,
        this
      );
    } catch (err) {
      this.setErrorStates(err);
      if (this.onError) {
        this.onError(err);
      }
      console.error(err);
    }

    if (this.isErrored) {
      return false;
    }
  }

  private setErrorStates(
    error: Error | null,
    data: AsyncCommand | null = null
  ) {
    if (error) {
      this.error = error;
      this.isErrored = true;
      this.isRunning = false;
    } else {
      this.error = null;
      this.isErrored = null;
    }

    if (data && data.status === 'errored') {
      this.error = data.result || 'n/a';
      this.isErrored = true;
      this.isRunning = false;
    }

    if (this.isErrored && this.onError) {
      this.onError(this.error);
    }
  }

  private complete() {
    this.isPending = false;
    this.isCompleted = true;
    this.isRunning = false;
    this.isErrored = false;
    this.error = null;

    if (this.onCompleted) {
      this.onCompleted(this.data);
    }
  }
}
